/**
 * Which locale the app shows, and why (REQ-02), plus the rules for keeping a device's choice
 * and the account's saved preference in step. Pure: no storage, no DOM, no clock.
 *
 * PRECEDENCE — the first one that names an ENABLED locale wins:
 *   1. `url`      an explicit locale in a public-page URL (`/es/...`), for that page only;
 *   2. `manual`   a language the player picked on THIS device (localStorage);
 *   3. `account`  the language saved on their account;
 *   4. `browser`  `navigator.languages` (or `Accept-Language` on the server), BCP 47-matched;
 *   5. `default`  English.
 *
 * A stored or saved locale that is not enabled (garbage, or a language later switched off) is
 * skipped rather than trusted, so it can never strand a player on a missing catalog — but it is
 * not ERASED either: if that language comes back, the choice comes back with it.
 */
import { DEFAULT_LOCALE, PSEUDO_LOCALE, canonicalLocaleCode, getEnabledLocaleCodes, type AppLocale, type LocaleCode } from "./localeConfig";
import { matchLocale } from "./localeMatch";

export type LocaleSource = "url" | "manual" | "account" | "browser" | "default";

export interface LocaleInputs {
  readonly urlLocale?: string | null;
  readonly manualLocale?: string | null;
  readonly accountLocale?: string | null;
  readonly browserLocales?: readonly string[];
}

export interface ResolveOptions {
  /** Locales that may be shown. Defaults to every `enabled` locale. */
  readonly available?: readonly LocaleCode[];
  /** Dev builds only: a manual pick of the pseudolocale is honoured. */
  readonly allowPseudo?: boolean;
}

export interface ResolvedLocale {
  readonly locale: AppLocale;
  readonly source: LocaleSource;
}

function pickExplicit(
  raw: string | null | undefined,
  available: readonly LocaleCode[],
  allowPseudo: boolean,
): AppLocale | null {
  if (!raw) return null;
  if (allowPseudo && raw.toLowerCase() === PSEUDO_LOCALE.toLowerCase()) return PSEUDO_LOCALE;
  const code = canonicalLocaleCode(raw);
  return code && available.includes(code) ? code : null;
}

export function resolveLocale(inputs: LocaleInputs, options: ResolveOptions = {}): ResolvedLocale {
  const available = options.available ?? getEnabledLocaleCodes();
  const allowPseudo = options.allowPseudo ?? false;

  // A URL never carries the pseudolocale: it is a dev tool, never a crawlable page.
  const url = pickExplicit(inputs.urlLocale, available, false);
  if (url) return { locale: url, source: "url" };

  const manual = pickExplicit(inputs.manualLocale, available, allowPseudo);
  if (manual) return { locale: manual, source: "manual" };

  const account = pickExplicit(inputs.accountLocale, available, false);
  if (account) return { locale: account, source: "account" };

  const browser = matchLocale(inputs.browserLocales ?? [], available);
  if (browser) return { locale: browser, source: "browser" };

  return { locale: DEFAULT_LOCALE, source: "default" };
}

/** A device's explicit choice, as persisted. `at` is epoch milliseconds. */
export interface StoredLocaleChoice {
  readonly locale: string;
  readonly at: number;
}

/** The account's saved preference. `updatedAt` is epoch milliseconds; null locale = automatic. */
export interface AccountLocalePreference {
  readonly locale: string | null;
  readonly updatedAt: number | null;
}

export type AccountSyncAction =
  | { readonly kind: "none" }
  | { readonly kind: "push"; readonly locale: string | null };

/**
 * SYNC RULES — what to write to the account when a signed-in player's device and account meet
 * (on sign-in, and after every manual change):
 *
 *   - The device's explicit choice is never overwritten by the account. It outranks the account
 *     in `resolveLocale`, and this function never returns a "pull".
 *   - A device choice that is NEWER than the account's saved one is pushed, so the player's
 *     other devices (the ones with no explicit choice of their own) follow it.
 *   - An account with nothing saved is seeded from the device's explicit choice.
 *   - A device with no explicit choice pushes nothing: browser detection is a guess about this
 *     device, not a preference to spread to the others.
 *   - The pseudolocale is never pushed.
 *
 * Clearing a choice ("Automatic") is pushed by the caller explicitly as `{ push, null }`, since
 * a device with nothing stored cannot tell "cleared just now" from "never chose".
 */
export function planAccountSync(
  device: StoredLocaleChoice | null,
  account: AccountLocalePreference | null,
): AccountSyncAction {
  if (!device) return { kind: "none" };
  if (device.locale.toLowerCase() === PSEUDO_LOCALE.toLowerCase()) return { kind: "none" };
  const code = canonicalLocaleCode(device.locale);
  if (!code) return { kind: "none" };

  if (!account || account.locale === null || account.updatedAt === null) {
    return account?.locale === code ? { kind: "none" } : { kind: "push", locale: code };
  }
  if (canonicalLocaleCode(account.locale) === code) return { kind: "none" };
  return device.at > account.updatedAt ? { kind: "push", locale: code } : { kind: "none" };
}
